<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import { AdminHandleDetails } from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'AdminHandleDetail' })

const route = useRoute()
const router = useRouter()
const notifyStore = useNotifyStore()

const loading = ref(true)
const revalidating = ref(false)
const handle = ref<AdminHandleDetails | null>(null)

const handleId = computed(() => Number(route.params.id))

const pageTitle = computed(() => handle.value?.external_name || 'Handle Details')
const pageSubtitle = computed(() => `Handle #${handleId.value}`)

const typeLabels: Record<string, string> = {
  person: 'Person',
  org: 'Organisation',
  role: 'Rolle'
}

async function fetchHandle() {
  loading.value = true
  try {
    const res = await useAxios<null, AdminHandleDetails>(
      'GET',
      `/admin/handle/${handleId.value}`,
      true
    )
    if (res && res.status === STATUS_CODES.OK && res.data) {
      const validate = AdminHandleDetails.safeParse(res.data)
      if (validate.success) {
        handle.value = validate.data
      } else {
        handle.value = res.data as AdminHandleDetails
      }
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminHandleDetail' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: 'Fehler',
      message: getErrorMessage(err as Error)
    })
  } finally {
    loading.value = false
  }
}

async function forceRevalidation() {
  revalidating.value = true
  try {
    const res = await useAxios<null, void>(
      'POST',
      `/admin/handle/${handleId.value}/revalidate`,
      true
    )
    if (res && (res.status === STATUS_CODES.OK || res.status === 204)) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: 'Erfolg',
        message: 'Revalidierung wurde angefordert.'
      })
      await fetchHandle()
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminHandleDetail:revalidate' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: 'Fehler',
      message: getErrorMessage(err as Error)
    })
  } finally {
    revalidating.value = false
  }
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-'
  return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm')
}

function getTypeClass(type: string): string {
  switch (type) {
    case 'person': return 'type-person'
    case 'org': return 'type-org'
    case 'role': return 'type-role'
    default: return ''
  }
}

function navigateToDomain(domainId: number) {
  router.push({ name: 'dashboardAdminDomainDetail', params: { id: domainId } })
}

onMounted(() => {
  fetchHandle()
})
</script>

<template>
  <BaseDashboardPage
    :title="pageTitle"
    :subtitle="pageSubtitle"
    icon="mdi-card-account-details"
    icon-color="purple"
    show-back
    :back-route="{ name: 'dashboardAdminHandles' }"
  >
    <template #actions>
      <v-btn
        color="warning"
        variant="outlined"
        :loading="revalidating"
        :disabled="loading"
        @click="forceRevalidation"
      >
        <v-icon icon="mdi-refresh" class="me-2" />
        Revalidierung anfordern
      </v-btn>
      <v-btn
        icon
        variant="text"
        :loading="loading"
        @click="fetchHandle"
      >
        <v-icon icon="mdi-refresh" />
      </v-btn>
    </template>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate size="48" color="primary" />
    </div>

    <!-- Content -->
    <div v-else-if="handle" class="detail-content">
      <!-- Info Cards Row -->
      <div class="info-grid">
        <!-- Handle Info Card -->
        <div class="info-card">
          <h3 class="card-title">Handle Info</h3>
          <div class="info-rows">
            <div class="info-row">
              <span class="info-label">Typ</span>
              <span class="type-badge" :class="getTypeClass(handle.type)">
                {{ typeLabels[handle.type] || handle.type }}
              </span>
            </div>
            <div v-if="handle.external_id" class="info-row">
              <span class="info-label">Externe ID</span>
              <span class="info-value">#{{ handle.external_id }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Account-Daten</span>
              <span class="info-value">
                <v-icon
                  :icon="handle.account_data ? 'mdi-check-circle' : 'mdi-close-circle'"
                  :color="handle.account_data ? 'success' : 'grey'"
                  size="18"
                />
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">Erstellt</span>
              <span class="info-value">{{ formatDate(handle.created) }}</span>
            </div>
            <div v-if="handle.updated" class="info-row">
              <span class="info-label">Aktualisiert</span>
              <span class="info-value">{{ formatDate(handle.updated) }}</span>
            </div>
            <div v-if="handle.imported_at" class="info-row">
              <span class="info-label">Importiert</span>
              <span class="info-value">{{ formatDate(handle.imported_at) }}</span>
            </div>
          </div>
        </div>

        <!-- Contact Details Card -->
        <div v-if="handle.name || handle.email" class="info-card">
          <h3 class="card-title">Kontaktdaten</h3>
          <div class="info-rows">
            <div v-if="handle.name" class="info-row">
              <span class="info-label">Name</span>
              <span class="info-value">{{ handle.name }}</span>
            </div>
            <div v-if="handle.org" class="info-row">
              <span class="info-label">Organisation</span>
              <span class="info-value">{{ handle.org }}</span>
            </div>
            <div v-if="handle.street" class="info-row">
              <span class="info-label">Straße</span>
              <span class="info-value">{{ handle.street }}</span>
            </div>
            <div v-if="handle.postcode || handle.city" class="info-row">
              <span class="info-label">Ort</span>
              <span class="info-value">{{ [handle.postcode, handle.city].filter(Boolean).join(' ') }}</span>
            </div>
            <div v-if="handle.state" class="info-row">
              <span class="info-label">Bundesland</span>
              <span class="info-value">{{ handle.state }}</span>
            </div>
            <div v-if="handle.country" class="info-row">
              <span class="info-label">Land</span>
              <span class="info-value">{{ handle.country }}</span>
            </div>
            <div v-if="handle.phone" class="info-row">
              <span class="info-label">Telefon</span>
              <span class="info-value">{{ handle.phone }}</span>
            </div>
            <div v-if="handle.fax" class="info-row">
              <span class="info-label">Fax</span>
              <span class="info-value">{{ handle.fax }}</span>
            </div>
            <div v-if="handle.email" class="info-row">
              <span class="info-label">E-Mail</span>
              <span class="info-value">{{ handle.email }}</span>
            </div>
          </div>
        </div>

        <!-- Account Card -->
        <div class="info-card">
          <h3 class="card-title">Account</h3>
          <div class="info-rows">
            <div class="info-row">
              <span class="info-label">E-Mail</span>
              <span class="info-value">{{ handle.account_email }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Account ID</span>
              <span class="info-value">#{{ handle.account_id }}</span>
            </div>
          </div>
        </div>

        <!-- Verification Card -->
        <div class="info-card verification-card">
          <h3 class="card-title">Verifizierung (NIS2)</h3>
          <div class="info-rows">
            <div class="info-row">
              <span class="info-label">E-Mail verifiziert</span>
              <span class="verification-status" :class="{ verified: handle.email_verified }">
                <v-icon :icon="handle.email_verified ? 'mdi-check-circle' : 'mdi-close-circle'" size="18" />
                {{ handle.email_verified ? 'Ja' : 'Nein' }}
              </span>
            </div>
            <div v-if="handle.email_verified_at" class="info-row">
              <span class="info-label">E-Mail verifiziert am</span>
              <span class="info-value">{{ formatDate(handle.email_verified_at) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Telefon verifiziert</span>
              <span class="verification-status" :class="{ verified: handle.phone_verified }">
                <v-icon :icon="handle.phone_verified ? 'mdi-check-circle' : 'mdi-close-circle'" size="18" />
                {{ handle.phone_verified ? 'Ja' : 'Nein' }}
              </span>
            </div>
            <div v-if="handle.phone_verified_at" class="info-row">
              <span class="info-label">Telefon verifiziert am</span>
              <span class="info-value">{{ formatDate(handle.phone_verified_at) }}</span>
            </div>
            <div v-if="handle.verification_deadline" class="info-row deadline-row">
              <span class="info-label">Verifizierungs-Deadline</span>
              <span class="info-value deadline-value">{{ formatDate(handle.verification_deadline) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Domains Section -->
      <div class="domains-section">
        <h2 class="section-title">Verknüpfte Domains</h2>
        <div class="domains-grid">
          <!-- Owner Domains -->
          <div class="domains-card">
            <div class="domains-header">
              <v-icon icon="mdi-account-circle" size="24" />
              <span class="domains-type">Owner Domains (owner-c)</span>
              <span class="domains-count">{{ handle.owner_domains?.length || 0 }}</span>
            </div>
            <div v-if="handle.owner_domains && handle.owner_domains.length > 0" class="domains-list">
              <div
                v-for="domain in handle.owner_domains"
                :key="domain.id"
                class="domain-item"
                @click="navigateToDomain(domain.id)"
              >
                <span class="domain-name">{{ domain.name }}</span>
                <v-icon icon="mdi-chevron-right" size="18" />
              </div>
            </div>
            <div v-else class="domains-empty">
              Keine Owner-Domains
            </div>
          </div>

          <!-- Admin Domains -->
          <div class="domains-card">
            <div class="domains-header">
              <v-icon icon="mdi-account-cog" size="24" />
              <span class="domains-type">Admin Domains (admin-c)</span>
              <span class="domains-count">{{ handle.admin_domains?.length || 0 }}</span>
            </div>
            <div v-if="handle.admin_domains && handle.admin_domains.length > 0" class="domains-list">
              <div
                v-for="domain in handle.admin_domains"
                :key="domain.id"
                class="domain-item"
                @click="navigateToDomain(domain.id)"
              >
                <span class="domain-name">{{ domain.name }}</span>
                <v-icon icon="mdi-chevron-right" size="18" />
              </div>
            </div>
            <div v-else class="domains-empty">
              Keine Admin-Domains
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Not Found -->
    <div v-else class="empty-state">
      <v-icon icon="mdi-alert-circle-outline" size="64" />
      <h3>Handle nicht gefunden</h3>
      <p>Das angeforderte Handle konnte nicht gefunden werden.</p>
      <v-btn
        color="primary"
        variant="outlined"
        @click="router.push({ name: 'dashboardAdminHandles' })"
      >
        Zurück zur Liste
      </v-btn>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Loading/Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
}

.empty-state {
  .v-icon {
    color: rgba(239, 83, 80, 0.5);
    margin-bottom: 16px;
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 8px;
  }

  p {
    font-size: 0.9rem;
    color: rgba(200, 218, 255, 0.5);
    margin: 0 0 24px;
  }
}

/* Content */
.detail-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

/* Cards */
.info-card {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  padding: 20px;

  &.verification-card {
    border-color: rgba(156, 39, 176, 0.2);
  }
}

.card-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 16px;
}

.info-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  &.deadline-row {
    padding-top: 8px;
    border-top: 1px solid rgba(120, 163, 255, 0.1);
    margin-top: 4px;
  }
}

.info-label {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
}

.info-value {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  text-align: right;

  &.deadline-value {
    font-weight: 500;
    color: #ffb74d;
  }
}

/* Type Badges */
.type-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;

  &.type-person {
    background: rgba(33, 150, 243, 0.15);
    color: #64b5f6;
  }

  &.type-org {
    background: rgba(156, 39, 176, 0.15);
    color: #ce93d8;
  }

  &.type-role {
    background: rgba(255, 152, 0, 0.15);
    color: #ffb74d;
  }
}

/* Verification Status */
.verification-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  color: rgba(239, 83, 80, 0.9);

  .v-icon {
    color: rgba(239, 83, 80, 0.9);
  }

  &.verified {
    color: rgba(76, 175, 80, 0.9);

    .v-icon {
      color: rgba(76, 175, 80, 0.9);
    }
  }
}

/* Domains Section */
.domains-section {
  margin-top: 8px;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 16px;
}

.domains-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.domains-card {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.domains-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  background: rgba(0, 0, 0, 0.15);
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);

  .v-icon {
    color: rgba(0, 176, 255, 0.8);
  }

  .domains-type {
    font-size: 0.9rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    flex: 1;
  }

  .domains-count {
    font-size: 0.85rem;
    font-weight: 500;
    color: rgba(0, 176, 255, 0.9);
    padding: 2px 10px;
    background: rgba(0, 176, 255, 0.1);
    border-radius: 10px;
  }
}

.domains-list {
  max-height: 300px;
  overflow-y: auto;
}

.domain-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  &:last-child {
    border-bottom: none;
  }

  .domain-name {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.9);
  }

  .v-icon {
    color: rgba(200, 218, 255, 0.4);
  }
}

.domains-empty {
  padding: 32px 20px;
  text-align: center;
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.4);
  font-style: italic;
}

/* Light Theme */
.v-theme--lightTheme {
  .info-card,
  .domains-card {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .card-title {
    color: rgba(15, 23, 42, 0.5);
  }

  .info-label {
    color: rgba(15, 23, 42, 0.5);
  }

  .info-value {
    color: rgba(15, 23, 42, 0.9);
  }

  .section-title {
    color: rgba(15, 23, 42, 0.9);
  }

  .domains-header {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(28, 110, 227, 0.1);

    .domains-type {
      color: rgba(15, 23, 42, 0.9);
    }
  }

  .domain-item {
    border-color: rgba(28, 110, 227, 0.05);

    .domain-name {
      color: rgba(15, 23, 42, 0.9);
    }

    .v-icon {
      color: rgba(15, 23, 42, 0.4);
    }
  }

  .domains-empty {
    color: rgba(15, 23, 42, 0.4);
  }

  .empty-state {
    h3 {
      color: rgba(15, 23, 42, 0.8);
    }

    p {
      color: rgba(15, 23, 42, 0.5);
    }
  }
}

/* Mobile */
@media (max-width: 768px) {
  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .info-value {
    text-align: left;
  }
}
</style>
